import { RubyGemMark } from '../components/RubyGemMark';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tenant, PricePlan } from '../types';
import { Search, Building, RefreshCw, Plus, X, Copy, Check, AlertCircle, Landmark, CheckCircle, Clock } from 'lucide-react';
import { api } from '../services/apiService';
import { useRevenueSummary, revenueMoney } from '../hooks/useRevenueSummary';

const Tenants: React.FC = () => {
    const navigate = useNavigate();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    const [lastOnboarded, setLastOnboarded] = useState<{tenantId: string, email: string, pass: string} | null>(null);
    const [showOnboardModal, setShowOnboardModal] = useState(false);

    const [newBizName, setNewBizName] = useState('');
    const [newOwnerName, setNewOwnerName] = useState('');
    const [newOwnerEmail, setNewOwnerEmail] = useState('');
    const [selectedPriceId, setSelectedPriceId] = useState('');
    const [prices, setPrices] = useState<PricePlan[]>([]);
    const [pricesLoading, setPricesLoading] = useState(false);

    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedTenantForPayment, setSelectedTenantForPayment] = useState<Tenant | null>(null);
    const [showPaymentLinkedModal, setShowPaymentLinkedModal] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const DEFAULT_TEMP_PASSWORD = 'Welcome@123';

    useEffect(() => {
        refreshData();
    }, []);

    useEffect(() => {
        if (showOnboardModal) {
            loadPrices();
        }
    }, [showOnboardModal]);

    const refreshData = async () => {
        setLoading(true);
        try {
            const tenantList = await api.tenants.getAll();
            setTenants(Array.isArray(tenantList) ? tenantList : []);
        } catch (err) {
            console.error('Failed to load tenants', err);
            setTenants([]);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (value: string, fieldId?: string) => {
        try {
            await navigator.clipboard.writeText(value);
            if (fieldId) {
                setCopiedField(fieldId);
                setTimeout(() => setCopiedField(null), 1500);
            }
        } catch (err) {
            console.error('Clipboard write failed', err);
        }
    };

    // Helper function to calculate trial days remaining
    const getTrialInfo = (tenant: Tenant): { isOnTrial: boolean; daysRemaining: number | null; trialEndDate: string | null } => {
        if (!tenant.trialEnd) {
            return { isOnTrial: false, daysRemaining: null, trialEndDate: null };
        }

        const now = Date.now();
        const trialEndMs = tenant.trialEnd;

        if (trialEndMs <= now) {
            return { isOnTrial: false, daysRemaining: null, trialEndDate: null };
        }

        const daysRemaining = Math.ceil((trialEndMs - now) / (1000 * 60 * 60 * 24));
        const trialEndDate = new Date(trialEndMs).toLocaleDateString();

        return { isOnTrial: true, daysRemaining, trialEndDate };
    };

    // Get status styling based on subscription status and trial info
    const getStatusStyle = (tenant: Tenant) => {
        const trialInfo = getTrialInfo(tenant);

        if (trialInfo.isOnTrial) {
            return {
                bg: 'bg-blue-100',
                text: 'text-blue-800',
                label: `Trial (${trialInfo.daysRemaining}d)`,
                tooltip: `Trial ends: ${trialInfo.trialEndDate}`
            };
        }

        switch (tenant.subscriptionStatus as string) {
            case 'active':
                return { bg: 'bg-green-100', text: 'text-green-800', label: 'ACTIVE', tooltip: null };
            case 'trialing':
                return { bg: 'bg-blue-100', text: 'text-blue-800', label: 'TRIALING', tooltip: null };
            case 'past_due':
                return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'PAST DUE', tooltip: null };
            case 'cancelled':
                return { bg: 'bg-red-100', text: 'text-red-800', label: 'CANCELLED', tooltip: null };
            default:
                return { bg: 'bg-gray-100', text: 'text-gray-800', label: tenant.subscriptionStatus?.toUpperCase() || 'UNKNOWN', tooltip: null };
        }
    };

    const handlePaymentClick = (tenant: Tenant) => {
        if (tenant.subscriptionStatus === 'active' || tenant.stripeSubscriptionId) {
            setShowPaymentLinkedModal(true);
        } else {
            handleSetupPayment(tenant);
        }
    };

    const loadPrices = async () => {
        setPricesLoading(true);
        try {
            const res = await api.superAdmin.listPrices();
            const fetched = Array.isArray(res?.items) ? res.items : [];
            setPrices(fetched);
            if (!selectedPriceId && fetched.length > 0) {
                setSelectedPriceId(fetched[0].id);
            }
        } catch (err) {
            console.error('Failed to load Stripe prices', err);
            setPrices([]);
        } finally {
            setPricesLoading(false);
        }
    };

    const formatPrice = (plan?: PricePlan) => {
        if (!plan || plan.unitAmount == null) return 'Unknown price';
        const amount = plan.unitAmount / 100;
        return `$${amount.toFixed(2)} / ${plan.interval}`;
    };

    const handleSetupPayment = (tenant: Tenant) => {
        setSelectedTenantForPayment(tenant);
        setShowPaymentModal(true);
        loadPrices();
    };

    const handleCreatePaymentSession = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTenantForPayment || !selectedPriceId) return;

        try {
            const { url } = await api.superAdmin.createTenantCheckoutSession({
                tenantId: selectedTenantForPayment.id,
                priceId: selectedPriceId,
                successUrl: `${window.location.origin}/admin/tenants?payment_success=true`,
                cancelUrl: `${window.location.origin}/admin/tenants?payment_cancelled=true`
            });
            if (url) {
                window.location.href = url;
            }
        } catch (err) {
            console.error('Failed to create payment session', err);
            alert('Failed to create payment session');
        }
    };

    const handleOnboard = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newBizName || !newOwnerEmail) {
            alert('Please fill all required fields');
            return;
        }

        const price = prices.find(p => p.id === selectedPriceId);
        const priceAmount = price?.unitAmount != null ? price.unitAmount / 100 : undefined;

        if (!price || priceAmount == null) {
            alert('Select a Stripe price before onboarding');
            return;
        }

        try {
            // Start onboarding process using the onboard service
            await api.onboard.createWorkspace({
                businessName: newBizName,
                ownerName: newOwnerName,
                ownerEmail: newOwnerEmail,
                priceId: selectedPriceId,
                priceAmount,
            });

            // Show success message with credentials
            setLastOnboarded({ tenantId: `tenant_${Date.now()}`, email: newOwnerEmail, pass: DEFAULT_TEMP_PASSWORD });
            setShowOnboardModal(false);
            resetForm();
            refreshData();
        } catch (error) {
            console.error('Failed to onboard:', error);
            alert('Onboarding failed: ' + (error as any).message);
        }
    };

    const resetForm = () => {
        setNewBizName('');
        setNewOwnerName('');
        setNewOwnerEmail('');
        if (prices.length > 0) {
            setSelectedPriceId(prices[0].id);
        } else {
            setSelectedPriceId('');
        }
    };

    const filteredTenants = (tenants || []).filter(t => 
        (t.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (t.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.ownerEmail || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const metrics = useMemo(() => {
        const totalTenants = tenants.length;
        const activeTenants = tenants.filter(t => (t.subscriptionStatus || '').toLowerCase() === 'active').length;
        // Count tenants currently on trial (have trialEnd in the future)
        const now = Date.now();
        const trialTenants = tenants.filter(t => t.trialEnd && t.trialEnd > now).length;
        return { totalTenants, activeTenants, trialTenants };
    }, [tenants]);

    /**
     * Money for this page. Never `tenant.subscriptionCost` — that field holds
     * the first line item of the first subscription, so a tenant with a second
     * location or a rented device was shown a fraction of what it pays.
     */
    const revenue = useRevenueSummary();
    const tenantMonthly = (tenantId?: string): string => {
        const row = revenue.summary?.tenants.find((t) => t.tenantId === tenantId);
        // An em dash rather than a guess: a wrong figure in a money column is
        // worse than an absent one.
        return row ? `${revenueMoney(row.billedCents)}/mo` : '—';
    };

    return (
        <div className="space-y-6 pb-20">
            {/* Header */}
            <div className="flex justify-between items-center gap-4 flex-wrap">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Tenant Management</h2>
                    <p className="text-gray-500">View and manage all tenant accounts</p>
                </div>
                <button
                    onClick={() => setShowOnboardModal(true)}
                    className="bg-coffee-600 text-white px-5 py-2.5 rounded-lg hover:bg-coffee-700 shadow-lg flex items-center font-bold transition-all transform active:scale-95"
                >
                    <Plus size={20} className="mr-2" /> Onboard Tenant
                </button>
            </div>

            {/* Success Banner */}
            {lastOnboarded && (
                <div className="bg-coffee-600 rounded-xl p-6 shadow-xl text-white animate-fade-in relative overflow-hidden">
                    <button onClick={() => setLastOnboarded(null)} className="absolute top-4 right-4 hover:bg-white/20 p-1 rounded-full"><X size={20}/></button>
                    <div className="flex items-start">
                        <div className="bg-white/20 p-3 rounded-xl mr-5 border border-white/30"><Check size={32} /></div>
                        <div className="flex-1">
                            <h3 className="text-xl font-bold mb-1 tracking-tight">Tenant Created Successfully!</h3>
                            <p className="text-blue-100 text-sm mb-4">Share these credentials with the owner:</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white/10 p-3 rounded-lg border border-white/20">
                                    <p className="text-[10px] text-blue-200 uppercase font-bold mb-1 tracking-widest">Tenant ID</p>
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono font-bold text-lg">{lastOnboarded.tenantId}</span>
                                        <button onClick={() => copyToClipboard(lastOnboarded.tenantId)} className="hover:text-blue-300 transition-colors"><Copy size={16}/></button>
                                    </div>
                                </div>
                                <div className="bg-white/10 p-3 rounded-lg border border-white/20">
                                    <p className="text-[10px] text-blue-200 uppercase font-bold mb-1 tracking-widest">Login Email</p>
                                    <div className="flex items-center justify-between">
                                        <span className="font-bold truncate max-w-[140px]">{lastOnboarded.email}</span>
                                        <button onClick={() => copyToClipboard(lastOnboarded.email)} className="hover:text-blue-300 transition-colors"><Copy size={16}/></button>
                                    </div>
                                </div>
                                <div className="bg-white/10 p-3 rounded-lg border border-white/20">
                                    <p className="text-[10px] text-blue-200 uppercase font-bold mb-1 tracking-widest">Initial Password</p>
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono font-bold text-lg">{lastOnboarded.pass}</span>
                                        <button onClick={() => copyToClipboard(lastOnboarded.pass)} className="hover:text-blue-300 transition-colors"><Copy size={16}/></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Metrics */}
            <div className="grid gap-4 md:grid-cols-3">
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-amber-50 text-amber-700"><Building size={20} /></div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-gray-500 font-bold">Total Tenants</p>
                        <p className="text-2xl font-bold text-gray-900">{metrics.totalTenants}</p>
                        <p className="text-xs text-gray-500">{metrics.activeTenants} active, {metrics.trialTenants} trial</p>
                    </div>
                </div>
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-green-50 text-green-700"><Check size={20} /></div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-gray-500 font-bold">Active Subscriptions</p>
                        {/* Subscriptions and billed locations, from billing-service.
                            This used to show the count of active TENANTS, which is a
                            different — and always smaller — number. */}
                        <p className="text-2xl font-bold text-gray-900">
                            {revenue.summary ? revenue.summary.counts.subscriptions : '—'}
                        </p>
                        <p className="text-xs text-gray-500">
                            {revenue.summary
                                ? `${revenue.summary.counts.billedLocations} billed locations · ${revenueMoney(revenue.summary.billed.totalCents)}/mo`
                                : 'Paying customers'}
                        </p>
                    </div>
                </div>
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-blue-50 text-blue-700"><Clock size={20} /></div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-gray-500 font-bold">On Trial</p>
                        <p className="text-2xl font-bold text-gray-900">{metrics.trialTenants}</p>
                        <p className="text-xs text-gray-500">Active trials</p>
                    </div>
                </div>
            </div>

            {/* Search & Controls */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 bg-gray-50 flex justify-between items-center border-b flex-wrap gap-3">
                    <div>
                        <h3 className="font-bold text-gray-700">Tenants</h3>
                        <p className="text-xs text-gray-500">Click on any tenant to view details and manage account</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                            <input
                                type="text"
                                placeholder="Search by name, ID, or email..."
                                className="pl-9 pr-4 py-2 border rounded-lg text-sm w-64 outline-none focus:ring-2 focus:ring-blue-500"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={refreshData}
                            disabled={loading}
                            className="p-2 hover:bg-gray-100 rounded transition"
                        >
                            {loading ? <RubyGemMark size={20} drawing color="currentColor" /> : <RefreshCw className="w-5 h-5 text-gray-600" />}
                        </button>
                    </div>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Business / Tenant ID</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Owner Email</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Plan</th>
                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-widest">Payment</th>
                            <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-widest">Action</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredTenants.map(tenant => (
                            <tr key={tenant.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-1.5 group">
                                        <span className="text-sm font-bold text-gray-900">{tenant.name}</span>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(tenant.name || '', `name-${tenant.id}`); }}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-100 rounded"
                                            title="Copy business name"
                                        >
                                            {copiedField === `name-${tenant.id}` ? <Check size={12} className="text-green-600" /> : <Copy size={12} className="text-gray-400" />}
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-1.5 group mt-0.5">
                                        <span className="text-[11px] text-gray-400 font-mono">{tenant.id}</span>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(tenant.id, `id-${tenant.id}`); }}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-100 rounded"
                                            title="Copy tenant ID"
                                        >
                                            {copiedField === `id-${tenant.id}` ? <Check size={12} className="text-green-600" /> : <Copy size={12} className="text-gray-400" />}
                                        </button>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-1.5 group">
                                        <span className="text-sm text-gray-700">{tenant.ownerEmail}</span>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(tenant.ownerEmail || '', `email-${tenant.id}`); }}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-100 rounded"
                                            title="Copy email"
                                        >
                                            {copiedField === `email-${tenant.id}` ? <Check size={12} className="text-green-600" /> : <Copy size={12} className="text-gray-400" />}
                                        </button>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    {(() => {
                                        const statusStyle = getStatusStyle(tenant);
                                        const trialInfo = getTrialInfo(tenant);
                                        return (
                                            <div className="flex flex-col gap-1">
                                                <span
                                                    className={`px-2 py-1 text-[10px] rounded-full font-bold uppercase ${statusStyle.bg} ${statusStyle.text} inline-flex items-center gap-1`}
                                                    title={statusStyle.tooltip || undefined}
                                                >
                                                    {trialInfo.isOnTrial && <Clock size={10} />}
                                                    {statusStyle.label}
                                                </span>
                                                {trialInfo.isOnTrial && (
                                                    <span className="text-[9px] text-gray-500">
                                                        Ends: {trialInfo.trialEndDate}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700 font-bold">{tenantMonthly(tenant.id)}</td>
                                <td className="px-6 py-4 text-center">
                                    <button
                                        onClick={() => handlePaymentClick(tenant)}
                                        className={`p-2 rounded-lg transition-colors ${
                                            tenant.subscriptionStatus === 'active' || tenant.stripeSubscriptionId
                                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                                        }`}
                                        title={tenant.subscriptionStatus === 'active' || tenant.stripeSubscriptionId ? 'Payment linked' : 'Setup payment'}
                                    >
                                        <Landmark size={18} />
                                    </button>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <button onClick={() => navigate(`/tenant/${tenant.id}`)} className="text-blue-600 font-bold hover:underline text-sm transition-colors">Manage</button>
                                </td>
                            </tr>
                        ))}
                        {filteredTenants.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-6 py-12 text-center text-gray-400 italic">No tenants found matching your search.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Onboard Modal */}
            {showOnboardModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                        <div className="p-6 bg-gray-50 border-b flex justify-between items-center">
                            <h3 className="text-xl font-bold text-gray-800 tracking-tight">Onboard New Tenant</h3>
                            <button onClick={() => setShowOnboardModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24}/></button>
                        </div>
                        <form onSubmit={handleOnboard} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1 tracking-widest">Business Name</label>
                                <input type="text" required className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500" value={newBizName} onChange={e => setNewBizName(e.target.value)} placeholder="e.g. Daily Grind Cafe"/>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1 tracking-widest">Owner Name</label>
                                    <input type="text" required className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-gray-900" value={newOwnerName} onChange={e => setNewOwnerName(e.target.value)} placeholder="Joe Smith"/>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1 tracking-widest">Owner Email</label>
                                    <input type="email" required className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-gray-900" value={newOwnerEmail} onChange={e => setNewOwnerEmail(e.target.value)} placeholder="joe@cafe.com"/>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1 tracking-widest">Temporary Password</label>
                                <input type="text" readOnly className="w-full p-2.5 border rounded-lg outline-none bg-gray-100 text-gray-700" value={DEFAULT_TEMP_PASSWORD} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1 tracking-widest">Select Stripe Plan</label>
                                <select
                                    required
                                    className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                                    value={selectedPriceId}
                                    onChange={e => setSelectedPriceId(e.target.value)}
                                    disabled={pricesLoading}
                                >
                                    {pricesLoading && <option value="">Loading prices...</option>}
                                    {!pricesLoading && prices.length === 0 && <option value="">No active prices found</option>}
                                    {!pricesLoading && prices.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.nickname || plan.productName || plan.id} — {formatPrice(plan)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex items-start text-xs text-blue-800">
                                <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                                <p><b>Note:</b> The owner will need to change their password on first login.</p>
                            </div>

                            <button type="submit" className="w-full bg-coffee-600 text-white py-3 rounded-lg font-bold shadow-lg hover:bg-coffee-700 transition-all transform active:scale-95">Create Tenant</button>
                        </form>
                    </div>
                </div>
            )}
            {/* Payment Setup Modal */}
            {showPaymentModal && selectedTenantForPayment && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                        <div className="p-6 bg-gray-50 border-b flex justify-between items-center">
                            <h3 className="text-xl font-bold text-gray-800 tracking-tight">Setup Payment</h3>
                            <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24}/></button>
                        </div>
                        <form onSubmit={handleCreatePaymentSession} className="p-6 space-y-4">
                            <div>
                                <p className="text-sm text-gray-600 mb-4">
                                    Setup subscription for <b>{selectedTenantForPayment.name || selectedTenantForPayment.id}</b>.
                                </p>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1 tracking-widest">Select Stripe Plan</label>
                                <select
                                    required
                                    className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                                    value={selectedPriceId}
                                    onChange={e => setSelectedPriceId(e.target.value)}
                                    disabled={pricesLoading}
                                >
                                    {pricesLoading && <option value="">Loading prices...</option>}
                                    {!pricesLoading && prices.length === 0 && <option value="">No active prices found</option>}
                                    {!pricesLoading && prices.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.nickname || plan.productName || plan.id} — {formatPrice(plan)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <button type="submit" className="w-full bg-coffee-600 text-white py-3 rounded-lg font-bold shadow-lg hover:bg-coffee-700 transition-all transform active:scale-95">
                                Proceed to Payment
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Payment Linked Confirmation Modal */}
            {showPaymentLinkedModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
                        <div className="p-6 text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle size={32} className="text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Payment Information Linked</h3>
                            <p className="text-gray-500 text-sm mb-6">This tenant's payment information is already connected and active.</p>
                            <button 
                                onClick={() => setShowPaymentLinkedModal(false)} 
                                className="w-full bg-coffee-600 text-white py-3 rounded-lg font-bold shadow-lg hover:bg-coffee-700 transition-all"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tenants;
